import axios from 'axios';

const signupClient = axios.create({
  baseURL: (process.env.REACT_APP_SERVER_URL || '').replace(/\/$/, ''),
  timeout: 15000,
});

export const normalizeSignupCode = (value) => String(value || '').trim().toUpperCase();
const hasControlCharacters = (value) => Array.from(value).some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127);
export function isValidSignupId(value) {
  const id = value.trim();
  return id.length >= 4 && id.length <= 30 && !/\s/.test(id) && !hasControlCharacters(id);
}

function passwordFits(value) {
  try { return encodeURIComponent(value).replace(/%[0-9A-F]{2}/g, '_').length <= 31; }
  catch { return false; }
}

async function postSignup(path, payload) {
  try {
    const { data } = await signupClient.post(path, payload);
    if (data?.success === false) throw new Error(data.error || '요청을 처리하지 못했습니다.');
    return data;
  } catch (error) {
    const message = error.response?.data?.error?.message || error.response?.data?.error;
    throw new Error(typeof message === 'string' ? message : error.code === 'ECONNABORTED'
      ? '응답 시간이 지났습니다. 잠시 후 다시 시도해 주세요.'
      : error.message || '서버에 연결하지 못했습니다. 다시 시도해 주세요.');
  }
}

export async function verifySignupCode(value) {
  const certCode = normalizeSignupCode(value);
  const data = await postSignup('/infoAca', { certCode });
  if (!Array.isArray(data) || !data.length) throw new Error('인증코드를 확인해 주세요.');
  return { certCode, name: String(data[0]?.acaName || '') };
}

export async function checkSignupId(certCode, id) {
  const data = await postSignup('/signup/check-id', { certCode: normalizeSignupCode(certCode), id: id.trim() });
  if (data?.success !== true || typeof data.available !== 'boolean') throw new Error('아이디 확인 결과를 읽지 못했습니다. 다시 확인해 주세요.');
  return data.available;
}

export async function submitSignup(input) {
  const data = await postSignup('/save/join/user', {
    certCode: normalizeSignupCode(input.certCode),
    id: input.userid.trim(),
    pw: input.password,
    userName: input.username.trim(),
    email: input.email.trim(),
    hp: input.phone.trim(),
    hp_parent: String(input.kind) === '1' ? input.phone_mom.trim() : '',
    kind: Number(input.kind),
    zipCode: input.zipcode,
    adress1: input.address.trim(),
    adress2: input.detailAddress.trim(),
    serviceTerms: '동의함',
    smsTerms: '동의함',
  });
  if (data?.success !== true && data?.success !== 'exist') throw new Error('회원가입 결과를 확인하지 못했습니다. 아이디 중복확인 후 다시 시도해 주세요.');
  return data;
}

export function validateSignup(input, { verifiedCode, checkedId, agreedTerms, agreedPrivacy, fixedCertCode = '' }) {
  const certCode = normalizeSignupCode(input.certCode);
  const id = input.userid.trim();
  if (!certCode || certCode.length > 50 || (fixedCertCode && certCode !== fixedCertCode)) return '인증코드를 확인해 주세요.';
  if (verifiedCode !== certCode) return '인증코드 확인을 완료해 주세요.';
  if (!['1', '2'].includes(String(input.kind))) return '학생 또는 선생님을 선택해 주세요.';
  if (!isValidSignupId(id)) return '아이디는 공백 없이 4~30자로 입력해 주세요.';
  if (!checkedId?.available || checkedId.id !== id || checkedId.certCode !== certCode) return '현재 아이디의 중복확인을 완료해 주세요.';
  if (!input.username.trim() || input.username.trim().length > 20) return '이름을 20자 이내로 입력해 주세요.';
  if (input.email.trim().length > 50 || !/^\S+@\S+\.\S+$/.test(input.email.trim())) return '이메일 주소를 50자 이내로 입력해 주세요.';
  if (!/^[0-9]{10,11}$/.test(input.phone.trim())) return '휴대전화 번호를 - 없이 10~11자리로 입력해 주세요.';
  if (String(input.kind) === '1' && !/^[0-9]{10,11}$/.test(input.phone_mom.trim())) return '부모님 휴대전화 번호를 - 없이 10~11자리로 입력해 주세요.';
  if (!input.address.trim() || input.address.trim().length > 100) return '주소를 검색해 주세요. 주소는 100자 이내로 입력할 수 있습니다.';
  if (!input.detailAddress.trim() || input.detailAddress.trim().length > 100) return '상세주소를 100자 이내로 입력해 주세요.';
  if (input.zipcode && !/^[0-9]{5,6}$/.test(input.zipcode)) return '주소를 다시 검색해 주세요.';
  if (input.password.length < 6 || hasControlCharacters(input.password)) return '비밀번호는 6자 이상 입력해 주세요.';
  if (!passwordFits(input.password)) return '비밀번호가 너무 깁니다. 영문·숫자는 최대 31자까지 입력할 수 있으며, 한글·특수문자는 더 짧게 입력해 주세요.';
  if (input.password !== input.confirmPassword) return '비밀번호가 일치하지 않습니다.';
  if (!agreedTerms || !agreedPrivacy) return '이용약관과 개인정보 수집 및 이용에 모두 동의해 주세요.';
  return '';
}
